/**
 * Verdienst-Anzeige und Minijob-Grenze. Mindestlohn und Verdienstgrenze
 * werden je Jahr konfiguriert (Tabelle wage_rules); hier die Vorgaben und
 * die reine Berechnung.
 */

export interface WageRule {
  year: number;
  /** Gesetzlicher Mindestlohn €/h */
  minWage: number;
  /** Minijob-Verdienstgrenze €/Monat */
  minijobLimit: number;
}

/** Vorgaben: Mindestlohn 2026 13,90 € (Grenze 603 €), 2027 14,60 € (Grenze 633 €). */
export const DEFAULT_WAGE_RULES: WageRule[] = [
  { year: 2026, minWage: 13.9, minijobLimit: 603 },
  { year: 2027, minWage: 14.6, minijobLimit: 633 },
];

/** Regel für ein Jahr; fehlt das Jahr, gilt das letzte davor (sonst das früheste). */
export function ruleForYear(rules: WageRule[], year: number): WageRule | null {
  if (!rules.length) return null;
  const sorted = [...rules].sort((a, b) => a.year - b.year);
  let hit: WageRule | null = null;
  for (const r of sorted) if (r.year <= year) hit = r;
  return hit ?? sorted[0];
}

export type EmploymentType = 'minijob' | 'sonstige';

export interface Earnings {
  minutes: number;
  hourlyWage: number;
  /** voraussichtliches Brutto in € (gerundet auf Cent) */
  gross: number;
  minijob: boolean;
  limit: number | null;
  /** Rest bis zur Grenze in € (negativ = überschritten) */
  remainingEuro: number | null;
  /** Rest bis zur Grenze in Minuten (0, wenn erreicht/überschritten) */
  remainingMinutes: number | null;
  exceeded: boolean;
  /** Soll-Stunden × Lohn läge über der Grenze */
  targetExceeds: boolean;
  belowMinWage: boolean;
  minWage: number | null;
}

const cents = (x: number) => Math.round(x * 100) / 100;

export function computeEarnings(opts: {
  minutes: number;
  hourlyWage: number;
  employment: EmploymentType;
  rule: WageRule | null;
  targetHoursMonth?: number | null;
}): Earnings {
  const gross = cents((opts.minutes / 60) * opts.hourlyWage);
  const minijob = opts.employment === 'minijob';
  const limit = minijob && opts.rule ? opts.rule.minijobLimit : null;
  const remainingEuro = limit === null ? null : cents(limit - gross);
  const remainingMinutes =
    limit === null || opts.hourlyWage <= 0 ? null : Math.max(0, Math.floor(((limit - gross) / opts.hourlyWage) * 60 + 1e-9));
  const target = opts.targetHoursMonth ?? null;
  return {
    minutes: opts.minutes,
    hourlyWage: opts.hourlyWage,
    gross,
    minijob,
    limit,
    remainingEuro,
    remainingMinutes,
    exceeded: limit !== null && gross > limit,
    targetExceeds: limit !== null && target !== null && cents(target * opts.hourlyWage) > limit,
    belowMinWage: !!opts.rule && opts.hourlyWage < opts.rule.minWage,
    minWage: opts.rule?.minWage ?? null,
  };
}

/** 1234.5 → '1.234,50 €' */
export function formatEuro(x: number): string {
  const [i, d] = Math.abs(x).toFixed(2).split('.');
  return `${x < 0 ? '−' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${d} €`;
}
